import { aplicarPromocion, formatearMoneda, lineaEnAlcance, type PromocionVista, type ReglaPromocion, type VentaLineaVista, type VentaVista } from '@barbones/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Cake, ChevronLeft, ChevronRight, CircleSlash, Package, PauseCircle, Plus, Scissors, Search, Tag, Trash2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api, mensajeError } from '@/api/cliente';
import { Button } from '@/components/ui/button';
import { Insignia } from '@/components/ui/data';
import { Campo, Input, Select, Textarea } from '@/components/ui/form';
import { Dialogo } from '@/components/ui/overlay';
import { useBarberos, useServicios } from '../agenda/datos';
import { useRefrescarCaja } from './datos';
import { avisarError } from '@/lib/avisos';

interface ProductoBusqueda {
  id: string;
  sku: string;
  nombre: string;
  precioVenta: number;
  disponible: number;
}

function AgregarConcepto({ venta, onCerrar }: { venta: VentaVista; onCerrar: () => void }) {
  const refrescar = useRefrescarCaja();
  const servicios = useServicios();
  const barberos = useBarberos();
  const [tipo, setTipo] = useState<'producto' | 'servicio'>('producto');
  const [servicioId, setServicioId] = useState('');
  const [barberoId, setBarberoId] = useState(venta.barbero?.id ?? '');
  const [busqueda, setBusqueda] = useState('');
  const [diferida, setDiferida] = useState('');
  const [cantidad, setCantidad] = useState(1);
  useEffect(() => {
    const t = setTimeout(() => setDiferida(busqueda.trim()), 250);
    return () => clearTimeout(t);
  }, [busqueda]);
  const productos = useQuery({
    queryKey: ['productos', 'vendibles', diferida],
    queryFn: () => api<ProductoBusqueda[]>('/productos/vendibles', { query: { q: diferida } }),
    enabled: tipo === 'producto',
  });

  const agregar = useMutation({
    mutationFn: (body: object) => api(`/ventas/${venta.id}/lineas`, { method: 'POST', body }),
    onSuccess: () => {
      refrescar();
      onCerrar();
    },
    onError: (e) => avisarError(e),
  });

  return (
    <Dialogo abierto onCambiarAbierto={(v) => !v && onCerrar()} titulo="Agregar al carrito" descripcion="La comisión de productos agregados en caja es de quien cobra.">
      <div className="mb-4 flex rounded-md border border-border p-0.5" role="group">
        {(['producto', 'servicio'] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTipo(t)}
            aria-pressed={tipo === t}
            className={`flex-1 rounded px-3 py-1.5 text-[13px] ${tipo === t ? 'bg-primary text-on-primary' : 'text-foreground-muted'}`}
          >
            {t === 'producto' ? 'Producto' : 'Servicio'}
          </button>
        ))}
      </div>
      {tipo === 'producto' ? (
        <div className="flex flex-col gap-3">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-foreground-muted" aria-hidden />
              <Input value={busqueda} onChange={(e) => setBusqueda(e.target.value)} placeholder="Nombre o SKU…" className="pl-9" aria-label="Buscar producto" autoFocus />
            </div>
            <Input type="number" min={1} value={cantidad} onChange={(e) => setCantidad(Math.max(1, Number(e.target.value)))} aria-label="Cantidad" className="cifras w-20" />
          </div>
          <ul className="flex max-h-72 flex-col gap-1 overflow-y-auto">
            {productos.data?.map((p) => (
              <li key={p.id}>
                <button
                  type="button"
                  disabled={p.disponible < cantidad || agregar.isPending}
                  onClick={() => agregar.mutate({ tipo: 'producto', productoId: p.id, cantidad })}
                  className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-left hover:bg-muted disabled:opacity-50"
                >
                  <Package className="size-4 text-foreground-muted" aria-hidden />
                  <span className="flex-1">
                    <span className="block text-[13.5px] font-medium">{p.nombre}</span>
                    <span className="cifras block text-[12px] text-foreground-muted">
                      {p.sku} · {p.disponible} disponibles
                    </span>
                  </span>
                  <span className="cifras text-[13.5px]">{formatearMoneda(p.precioVenta)}</span>
                </button>
              </li>
            ))}
            {productos.data?.length === 0 && <li className="px-3 py-4 text-center text-[13px] text-foreground-muted">Sin productos disponibles</li>}
          </ul>
        </div>
      ) : (
        <div className="grid gap-3">
          <Campo etiqueta="Servicio">
            {(p) => (
              <Select {...p} value={servicioId} onChange={(e) => setServicioId(e.target.value)}>
                <option value="">Elige un servicio…</option>
                {servicios.data?.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.nombre} · {formatearMoneda(s.precio)}
                  </option>
                ))}
              </Select>
            )}
          </Campo>
          <Campo etiqueta="Barbero que lo realizó">
            {(p) => (
              <Select {...p} value={barberoId} onChange={(e) => setBarberoId(e.target.value)}>
                <option value="">Elige un barbero…</option>
                {barberos.data?.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.nombre}
                  </option>
                ))}
              </Select>
            )}
          </Campo>
          <Button disabled={!servicioId || !barberoId} cargando={agregar.isPending} onClick={() => agregar.mutate({ tipo: 'servicio', servicioId, barberoId })}>
            <Plus /> Agregar servicio
          </Button>
        </div>
      )}
    </Dialogo>
  );
}

/** Conceptos de la venta seleccionada con acciones de suspender y cancelar. */
const describirPromo = (p: PromocionVista) =>
  p.tipo === 'porcentaje' ? `${p.valor / 100}% de descuento` : p.tipo === 'monto' ? `${formatearMoneda(p.valor)} menos por pieza` : `Paquete a ${formatearMoneda(p.valor)}`;

/** Línea de la venta en el formato que usa el cálculo de promociones. */
const comoPromocionable = (l: VentaLineaVista) => ({ id: l.id, tipo: l.tipo, servicioId: l.servicioId, productoId: l.productoId, total: l.total, cantidad: l.cantidad });

const reglaDe = (p: PromocionVista): ReglaPromocion =>
  p.tipo === 'paquete'
    ? { tipo: 'paquete', valor: p.valor, alcance: { servicioIds: [], productoIds: [] }, serviciosPaquete: p.servicioIds }
    : { tipo: p.tipo, valor: p.valor, alcance: { servicioIds: p.servicioIds, productoIds: p.productoIds } };

/** Descuento que daría la promoción sobre las líneas indicadas (o todo el carrito). */
const descuentoPrevisto = (p: PromocionVista, lineas: VentaLineaVista[]) =>
  aplicarPromocion(lineas.map(comoPromocionable), reglaDe(p)).reduce((s, d) => s + d.descuento, 0);

/**
 * Elegir una promoción autorizada y vigente, el descuento de cumpleaños, o quitar la actual.
 * Salvo los paquetes, la promoción pide después si va a todo el carrito o a artículos elegidos.
 */
function ElegirPromocion({ venta, onCerrar }: { venta: VentaVista; onCerrar: () => void }) {
  const refrescar = useRefrescarCaja();
  const promos = useQuery({ queryKey: ['promociones'], queryFn: () => api<PromocionVista[]>('/promociones') });
  const vigentes = promos.data?.filter((p) => p.vigente) ?? [];
  const [elegida, setElegida] = useState<PromocionVista | null>(null);
  const [modo, setModo] = useState<'todo' | 'articulos'>('todo');
  const [marcadas, setMarcadas] = useState<string[]>([]);
  const aplicar = useMutation({
    mutationFn: (v: { ruta: '/promocion' | '/cumpleanos'; promocionId?: string; lineaIds?: string[]; quitar?: boolean }) =>
      api<VentaVista>(`/ventas/${venta.id}${v.ruta}`, {
        method: v.quitar ? 'DELETE' : 'POST',
        body: v.promocionId ? { promocionId: v.promocionId, ...(v.lineaIds?.length ? { lineaIds: v.lineaIds } : {}) } : undefined,
      }),
    onSuccess: (v, vars) => {
      refrescar();
      if (vars.quitar) toast.success('Promoción quitada');
      else if (v.descuento === 0) toast.warning('La promoción no aplica a lo que hay en el carrito');
      else toast.success(`Descuento aplicado: ${formatearMoneda(v.descuento)}`);
      onCerrar();
    },
    onError: (e) => avisarError(e),
  });
  const opcion = 'flex min-h-11 w-full cursor-pointer items-center gap-3 rounded-md border border-border px-3 py-2.5 text-left transition-colors hover:border-accent disabled:cursor-not-allowed disabled:opacity-50';

  const elegir = (p: PromocionVista) => {
    if (p.tipo === 'paquete') return aplicar.mutate({ ruta: '/promocion', promocionId: p.id });
    setElegida(p);
    setModo('todo');
    setMarcadas([]);
  };

  if (elegida) {
    const alcance = reglaDe(elegida).alcance;
    const candidatas = venta.lineas.filter((l) => lineaEnAlcance(l, alcance));
    const seleccion = modo === 'todo' ? venta.lineas : candidatas.filter((l) => marcadas.includes(l.id));
    const total = descuentoPrevisto(elegida, seleccion);
    const alternar = (id: string) => setMarcadas((m) => (m.includes(id) ? m.filter((x) => x !== id) : [...m, id]));
    return (
      <Dialogo
        abierto
        ancho="sm"
        onCambiarAbierto={(v) => !v && onCerrar()}
        titulo={elegida.nombre}
        descripcion={`${describirPromo(elegida)}. ¿Dónde la aplicas?`}
        pie={
          <>
            <Button variante="fantasma" onClick={() => setElegida(null)} disabled={aplicar.isPending}>
              <ChevronLeft /> Atrás
            </Button>
            <Button
              disabled={aplicar.isPending || total === 0}
              onClick={() => aplicar.mutate({ ruta: '/promocion', promocionId: elegida.id, lineaIds: modo === 'articulos' ? seleccion.map((l) => l.id) : undefined })}
            >
              Aplicar · <span className="tabular-nums">{formatearMoneda(total)}</span>
            </Button>
          </>
        }
      >
        <fieldset className="grid gap-2">
          <legend className="sr-only">Alcance de la promoción</legend>
          {(['todo', 'articulos'] as const).map((m) => (
            <label key={m} className={`${opcion} ${modo === m ? 'border-accent' : ''}`}>
              <input type="radio" name="alcance-promo" className="accent-[var(--bb-accent)]" checked={modo === m} onChange={() => setModo(m)} />
              <span className="min-w-0 flex-1">
                <span className="block text-[14px] font-medium">{m === 'todo' ? 'Todo el carrito' : 'Elegir artículos'}</span>
                <span className="block text-[12.5px] text-foreground-muted">
                  {m === 'todo' ? `Todo lo que entra en la promoción (${candidatas.length})` : 'Solo los artículos que marques'}
                </span>
              </span>
            </label>
          ))}
        </fieldset>
        {modo === 'articulos' && (
          <fieldset className="mt-3 grid gap-1.5">
            <legend className="mb-1.5 text-[12.5px] font-medium text-foreground-muted">Artículos que entran en la promoción</legend>
            {candidatas.map((l) => (
              <label key={l.id} className={opcion}>
                <input type="checkbox" className="accent-[var(--bb-accent)]" checked={marcadas.includes(l.id)} onChange={() => alternar(l.id)} />
                <span className="min-w-0 flex-1 text-[14px]">
                  {l.descripcion}
                  {l.cantidad > 1 && <span className="text-foreground-muted"> × {l.cantidad}</span>}
                </span>
                <span className="text-[13px] tabular-nums text-success">−{formatearMoneda(descuentoPrevisto(elegida, [l]))}</span>
              </label>
            ))}
          </fieldset>
        )}
      </Dialogo>
    );
  }

  return (
    <Dialogo abierto ancho="sm" onCambiarAbierto={(v) => !v && onCerrar()} titulo="Aplicar promoción" descripcion="Solo aparecen las promociones autorizadas por el dueño y vigentes hoy.">
      <div className="grid gap-2">
        {vigentes.map((p) => {
          const aplica = descuentoPrevisto(p, venta.lineas) > 0;
          return (
            <button key={p.id} type="button" className={opcion} disabled={aplicar.isPending || !aplica} onClick={() => elegir(p)}>
              <Tag className="size-4 shrink-0 text-accent" aria-hidden />
              <span className="min-w-0 flex-1">
                <span className="block text-[14px] font-medium">{p.nombre}</span>
                <span className="block text-[12.5px] text-foreground-muted">{aplica ? describirPromo(p) : 'No aplica a lo que hay en el carrito'}</span>
              </span>
              {aplica && p.tipo !== 'paquete' && <ChevronRight className="size-4 shrink-0 text-foreground-muted" aria-hidden />}
            </button>
          );
        })}
        {!promos.isLoading && vigentes.length === 0 && <p className="text-[13px] text-foreground-muted">No hay promociones vigentes.</p>}
        {venta.cliente && (
          <button type="button" className={opcion} disabled={aplicar.isPending} onClick={() => aplicar.mutate({ ruta: '/cumpleanos' })}>
            <Cake className="size-4 shrink-0 text-accent" aria-hidden />
            <span className="text-[14px] font-medium">Descuento de cumpleaños de {venta.cliente.nombre}</span>
          </button>
        )}
        {venta.promocion && (
          <Button variante="fantasma" className="justify-start text-destructive" onClick={() => aplicar.mutate({ ruta: '/promocion', quitar: true })}>
            <X /> Quitar «{venta.promocion.nombre}»
          </Button>
        )}
      </div>
    </Dialogo>
  );
}

export function Carrito({ venta, onCerrarVenta }: { venta: VentaVista; onCerrarVenta: () => void }) {
  // El servicio sigue en la silla: se pueden sumar productos, pero no suspender ni cancelar.
  const enServicio = venta.estado === 'abierta';
  const refrescar = useRefrescarCaja();
  const [agregar, setAgregar] = useState(false);
  const [promo, setPromo] = useState(false);
  const [cancelar, setCancelar] = useState(false);
  const [motivo, setMotivo] = useState('');

  const accion = useMutation({
    mutationFn: ({ ruta, body }: { ruta: string; body?: object }) => api(`/ventas/${venta.id}${ruta}`, { method: ruta.startsWith('/lineas/') ? 'DELETE' : 'POST', body }),
    onSuccess: (_, { ruta }) => {
      refrescar();
      if (ruta === '/suspender') {
        toast.info('Venta suspendida: la puedes retomar en «Suspendidas»');
        onCerrarVenta();
      }
      if (ruta === '/cancelar') {
        toast.success('Venta cancelada');
        setCancelar(false);
        onCerrarVenta();
      }
    },
    onError: (e) => avisarError(e),
  });

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-start justify-between gap-2 border-b border-border px-4 py-3">
        <div>
          <p className="font-display text-[18px] leading-tight">{venta.cliente?.nombre ?? 'Venta de mostrador'}</p>
          <p className="text-[12.5px] text-foreground-muted">
            Folio {venta.folio}
            {venta.barbero ? ` · Atendió ${venta.barbero.nombre}` : ''}
          </p>
        </div>
        {enServicio ? (
          <Insignia tono="aviso">
            <Scissors aria-hidden /> En servicio
          </Insignia>
        ) : (
          <Insignia tono={venta.origen === 'app' ? 'acento' : 'neutro'}>{venta.origen === 'app' ? 'Desde la app' : 'Mostrador'}</Insignia>
        )}
      </div>
      <ul className="flex-1 divide-y divide-border/70 overflow-y-auto">
        {venta.lineas.map((l) => (
          <li key={l.id} className="flex items-center gap-3 px-4 py-2.5">
            {l.tipo === 'servicio' ? <Scissors className="size-4 text-accent" aria-hidden /> : <Package className="size-4 text-foreground-muted" aria-hidden />}
            <div className="min-w-0 flex-1">
              <p className="truncate text-[13.5px] font-medium">
                {l.cantidad > 1 && <span className="cifras">{l.cantidad}× </span>}
                {l.descripcion}
              </p>
              <p className="cifras text-[12px] text-foreground-muted">
                {l.sku ? `${l.sku} · ` : ''}
                {formatearMoneda(l.precioUnitario)} c/u{l.comisionista ? ` · comisión: ${l.comisionista.nombre}` : ''}
              </p>
            </div>
            <span className="cifras text-right text-[14px]">
              {l.descuento > 0 && <span className="block text-[11.5px] text-foreground-muted line-through">{formatearMoneda(l.total)}</span>}
              {formatearMoneda(l.total - l.descuento)}
            </span>
            {!l.fija && (
              <Button variante="fantasma" tamano="icono" onClick={() => accion.mutate({ ruta: `/lineas/${l.id}` })} aria-label={`Quitar ${l.descripcion}`}>
                <Trash2 />
              </Button>
            )}
          </li>
        ))}
        {venta.lineas.length === 0 && <li className="px-4 py-10 text-center text-[13px] text-foreground-muted">Carrito vacío</li>}
      </ul>
      {venta.promocion && (
        <div className="flex items-center gap-2 border-t border-border bg-accent-soft/60 px-4 py-2 text-[13px]">
          <Tag className="size-4 text-accent" aria-hidden />
          <span className="flex-1">{venta.promocion.nombre}</span>
          <span className="cifras font-medium text-accent">−{formatearMoneda(venta.descuento)}</span>
        </div>
      )}
      <div className="flex flex-wrap gap-2 border-t border-border p-3">
        <Button variante="secundario" onClick={() => setAgregar(true)}>
          <Plus /> Agregar
        </Button>
        <Button variante="secundario" onClick={() => setPromo(true)} disabled={venta.lineas.length === 0}>
          <Tag /> Promoción
        </Button>
        {!enServicio && (
          <>
            <Button variante="secundario" onClick={() => accion.mutate({ ruta: '/suspender' })}>
              <PauseCircle /> Suspender
            </Button>
            <Button variante="fantasma" className="ml-auto text-destructive" onClick={() => setCancelar(true)}>
              <CircleSlash /> Cancelar
            </Button>
          </>
        )}
      </div>
      {agregar && <AgregarConcepto venta={venta} onCerrar={() => setAgregar(false)} />}
      {promo && <ElegirPromocion venta={venta} onCerrar={() => setPromo(false)} />}
      {cancelar && (
        <Dialogo
          abierto
          ancho="sm"
          onCambiarAbierto={setCancelar}
          titulo="Cancelar venta"
          pie={
            <>
              <Button variante="secundario" onClick={() => setCancelar(false)}>
                Volver
              </Button>
              <Button variante="peligro" disabled={!motivo.trim()} cargando={accion.isPending} onClick={() => accion.mutate({ ruta: '/cancelar', body: { motivo } })}>
                Cancelar venta
              </Button>
            </>
          }
        >
          <Campo etiqueta="Motivo" requerido>
            {(p) => <Textarea {...p} value={motivo} onChange={(e) => setMotivo(e.target.value)} autoFocus />}
          </Campo>
        </Dialogo>
      )}
    </div>
  );
}
